import { Menu } from "@headlessui/react";
import { useEffect, useContext } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { EyeIcon, PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import CompanyOfficialSubRow from "components/CompanyDashboard/BusinessDetails/CompanyOfficialSubRow";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import Pagination from "components/lib/Shared/Pagination";
import TableActionMenuWrapper from "components/lib/Shared/TableActionMenuWrapper";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import { Constants } from "config/constants";
import { format } from "date-fns";
import { useToast } from "hooks/useToast";
import Dialog from "components/lib/Shared/Dialog";
import Button from "components/lib/Shared/Button";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useExpanded, useTable } from "react-table";
import { twMerge } from "tailwind-merge";
import { getFullName } from "utils";
import ChangeOfParticularsSubRow from "./ChangeOfParticularsSubRow";
import {
  deleteInitiatedTransactionChangeAsync,
  getAllInitiateTransactionChangeAsync,
  editInitiatedTransactionChangeAsync,
  recallInitiatedTransactionChangeAsync,
} from "state/slices/initiateTransactionChange";
import TransactionEditContext from "context/TransactionEditContext";
import ChangesContext from "context/ChangesContext";
import { tr } from "date-fns/locale";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";

const ManageChangeOfParticularsTable = ({
  companyId,
  data,
  totalCount,
  currentTab,
  isLoading = false,
  typeOfTable = "",
  changeList,
  setChangeList,
  setOfficialsList,
  isOfficialsListTable,
  isChangeOfParticularsTable,
  dataRowStyle,
  updateTable,
  setUpdateTable,
}) => {
  const dispatch = useDispatch();
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const {
    transactionToEdit,
    isInTransactionEditContext,
    setTransactionToEdit,
  } = useContext(TransactionEditContext);
  const {
    transactionId,
    setTransactionId,
    transactionPayload,
    setTransactionPayload,
  } = useContext(ManageModuleContext);

  const [selectedRow, setSelectedRow] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [initialRows, setInitialRows] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [actionType, setActionType] = useState("");
  const [rowsPerPage] = useState(10);

  const columns = useMemo(() => {
    const tempTabs = [
      {
        Header: () => "Full Name",
        id: "Name",
        accessor: "fullName",
        Cell: ({ row: { original } }) => {
          const { names, fileUploads } = original;
          const fullName = getFullName(names) || "N/A";
          if (
            CORPORATES.includes(original.idType) ||
            (original.designation === "Joint Shareholder" &&
              original.isDirector !== "Yes")
          ) {
            return <h1>{fullName}</h1>;
          }
          return (
            <div className="flex items-center space-x-1">
              <div className={`flex-shrink-0`}>
                {fileUploads?.passportPhoto ? (
                  <img
                    className="w-8 h-8 rounded-full"
                    src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${fileUploads?.passportPhoto}`}
                    alt={fullName}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full">
                    <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                      <AvatarIcon />
                    </div>
                  </div>
                )}
              </div>
              <div className="px-3">
                <h1 className="">{fullName}</h1>
              </div>
            </div>
          );
        },
        // We can render something different for subRows
        SubCell: (cellProps) => <>{cellProps.value}</>,
      },
      {
        Header: () => "ID Type",
        id: "ID Type",
        accessor: "idType",
      },
      ...(typeOfTable === "recent-updates"
        ? [
            {
              Header: () => "Brs Ref",
              id: "BRS REF",
              Cell: ({ row }) =>
                row.original?.brsRef ? row.original?.brsRef : "-",
            },
            {
              Header: () => "Date",
              id: "DATE",
              Cell: ({ row }) =>
                row.original?.createdAt
                  ? format(new Date(row.original?.createdAt), "yyyy/MM/dd")
                  : "N/A",
            },
          ]
        : []),
      {
        Header: () => "Designation",
        id: "Designation",
        Cell: ({ row: { original } }) => {
          return Array.isArray(original.fullName)
            ? "Joint Shareholder"
            : original.isDirector === "Yes"
            ? "Director"
            : original.designation ?? "";
        },
      },
      {
        Header: () => "Status",
        id: "manageModuleStatus",
        Cell: ({ row: { original } }) =>
          original?.manageModuleStatus ? original?.manageModuleStatus : "-",
      },
      {
        Header: "Action",
        Cell: ({ row }) => {
          return (
            <ActionButtons
              actionType={actionType}
              isOfficialsListTable={isOfficialsListTable}
              isChangeOfParticularsTable={isChangeOfParticularsTable}
              setActionType={setActionType}
              setSelectedRow={setSelectedRow}
              setShowConfirmationDialog={setShowConfirmationDialog}
              row={row}
            />
          );
        },
      },
    ];
    return tempTabs;
  }, []);

  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      if (!actionType)
        return (
          <tr>
            <td></td>
          </tr>
        );
      if (actionType === "change" || actionType === "edit") {
        return (
          <ChangeOfParticularsSubRow
            row={row}
            visibleColumns={visibleColumns}
            changeList={changeList}
            setChangeList={setChangeList}
            actionType={actionType}
          />
        );
      } else {
        return (
          <CompanyOfficialSubRow
            row={row}
            rowProps={rowProps}
            visibleColumns={visibleColumns}
            currentTab={currentTab}
            initialRows={initialRows}
          />
        );
      }
    },
    [currentTab, initialRows, actionType]
  );
  useEffect(() => {
    dispatch(
      getAllInitiateTransactionChangeAsync({
        companyId,
        params: {
          limit: 10,
          skip: (1 - 1) * 10,
          search: "",
          sort: "updatedAt",
          order: "desc",
        },
      })
    );
  }, [updateTable]);
  return (
    <div className="w-full bg-white shadow">
      <div className="w-full">
        <ManageTable
          columns={columns}
          data={data}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
          paginate
          companyId={companyId}
          dataRowCellStyle="whitespace-normal"
          renderRowSubComponent={renderRowSubComponent}
          actionType={actionType}
          isLoading={isLoading}
          typeOfTable={typeOfTable}
          dataRowStyle={dataRowStyle}
        />
      </div>
      {showConfirmationDialog && (
        <ConfirmationModal
          setShowConfirmationDialog={setShowConfirmationDialog}
          selectedRow={selectedRow}
          setUpdateTable={setUpdateTable}
          transactionToEdit={transactionToEdit}
          transactionId={transactionId}
          initiatedTransactions={initiatedTransactions.items}
          setChangeList={setChangeList}
          setOfficialsList={setOfficialsList}
          isInTransactionEditContext={isInTransactionEditContext}
          setTransactionToEdit={setTransactionToEdit}
        />
      )}
    </div>
  );
};

const ActionButtons = ({
  actionType,
  isOfficialsListTable,
  isChangeOfParticularsTable,
  setActionType,
  setSelectedRow,
  setShowConfirmationDialog,
  row,
}) => {
  const params = useParams();

  return (
    <div>
      <TableActionMenuWrapper>
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              onClick={() => {
                row.toggleRowExpanded();
                setActionType("view");
              }}
              className={`${
                active ? "bg-violet-500 text-white" : "text-gray-900"
              } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
            >
              {row.isExpanded ? (
                <>
                  <span className="ml-4">Close</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                  View
                </>
              )}
            </button>
          )}
        </Menu.Item>
        {isOfficialsListTable && (
          <Menu.Item>
            {({ active }) => (
              <button
                type="button"
                onClick={() => {
                  row.toggleRowExpanded();
                  setActionType("change");
                }}
                className={`${
                  active ? "bg-violet-500 text-white" : "text-gray-900"
                } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
              >
                <PencilAltIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Change
              </button>
            )}
          </Menu.Item>
        )}
        {isChangeOfParticularsTable && (
          <>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => {
                    row.toggleRowExpanded();
                    setActionType("edit");
                  }}
                  className={`${
                    active ? "bg-violet-500 text-white" : "text-gray-900"
                  } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
                >
                  <PencilAltIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                  <span className="text-red-500 font-medium"> Edit </span>
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmationDialog(true);
                    setSelectedRow(row);
                    setActionType("delete");
                  }}
                  className={`${
                    active ? "bg-violet-500 text-white" : "text-gray-900"
                  } group flex rounded-md items-center w-full px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
                >
                  <TrashIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                  <span className="text-red-500 font-medium"> Delete </span>
                </button>
              )}
            </Menu.Item>
          </>
        )}
      </TableActionMenuWrapper>
    </div>
  );
};

export default ManageChangeOfParticularsTable;

const ManageTable = ({
  columns,
  data,
  renderRowSubComponent,
  headingStyle,
  headingRowStyle,
  dataRowStyle,
  dataRowCellStyle,
  hideColumnsRow,
  isLoading,
  currentPage,
  rowsPerPage = 10,
  showSnColumn = true,
  totalRows,
  setCurrentPage,
  paginate,
  typeOfTable,
}) => {
  const tableColumns = useMemo(() => {
    if (!showSnColumn) {
      return columns;
    }

    return [
      {
        Header: "Sn",
        accessor: "sn",
      },
      ...columns,
    ];
  }, [showSnColumn, columns]);

  const tableData = useMemo(() => {
    return showSnColumn
      ? data.map((item, index) => {
          return {
            ...item,
            sn:
              item.id === "total"
                ? ""
                : currentPage === undefined
                ? index + 1
                : index + 1 + (currentPage - 1) * rowsPerPage,
          };
        })
      : data;
  }, [showSnColumn, data, currentPage, rowsPerPage]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,
    visibleColumns,
  } = useTable(
    {
      columns: tableColumns,
      data: tableData,
      autoResetExpanded: false,
    },
    useExpanded // We can useExpanded to track the expanded state
    // for sub components too!
  );

  // Main Table here
  return (
    <div className="">
      <div className="w-full overflow-auto">
        <table className="min-w-full min-h-full" {...getTableProps()}>
          {!hideColumnsRow && (
            <thead className={"bg-[#31415d] align-top"}>
              {headerGroups.map((headerGroup) => (
                <tr
                  {...headerGroup.getHeaderGroupProps()}
                  className={headingRowStyle}
                >
                  {headerGroup.headers.map((column) => (
                    <th
                      scope="col"
                      className={twMerge(
                        "px-6 py-3 text-left text-sm font-medium tracking-wider text-slate-50",
                        headingStyle,
                        column.headingStyle
                      )}
                      {...column.getHeaderProps()}
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody
            className="align-top bg-white relative"
            {...getTableBodyProps()}
          >
            {data.length === 0 && typeOfTable === "changeOfParticulars" && (
              <tr>
                <td className="text-center py-4" colSpan={columns.length + 1}>
                  No officials to change particulars
                </td>
              </tr>
            )}
            {rows.map((row, i) => {
              row["toggleAllRowsExpanded"] = toggleAllRowsExpanded;
              prepareRow(row);
              const rowProps = row.getRowProps();
              return (
                // Use a React.Fragment here so the table markup is still valid
                <React.Fragment key={rowProps.key}>
                  <tr
                    {...rowProps}
                    className={twMerge(
                      dataRowStyle,
                      data.length === row.index + 1 ? "border-b-0" : ""
                    )}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td
                          className={twMerge(
                            "px-6 py-4 text-sm text-primary",
                            dataRowCellStyle,
                            cell.column.className
                          )}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {/* We could pass anything into this */}
                  {row.isExpanded &&
                    renderRowSubComponent({
                      row,
                      rowProps,
                      visibleColumns,
                    })}{" "}
                  {/* Remove actionType here */}
                </React.Fragment>
              );
            })}
            {isLoading && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-gray-200/50 z-10"
                id="loading-overlay"
              >
                <SpinnerIcon textColor="text-gray-400" />
              </div>
            )}
          </tbody>
        </table>
      </div>
      {paginate && totalRows > Constants.PAGINATION_LIMIT && (
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          setCurrentPage={setCurrentPage}
          className="mt-0"
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

const ConfirmationModal = ({
  selectedRow,
  setShowConfirmationDialog,
  setUpdateTable,
  transactionToEdit,
  transactionId,
  initiatedTransactions,
  setChangeList,
  setOfficialsList,
  isInTransactionEditContext,
  setTransactionToEdit,
}) => {
  const [modelText, setModelText] = useState({
    heading: "Your details will be deleted",
    text: "Are you sure you want to delete the transaction?",
  });
  const dispatch = useDispatch();
  const { toast } = useToast(5000);
  const history = useHistory();
  const officialId = selectedRow?.original?._id;

  if (!initiatedTransactions || !Array.isArray(initiatedTransactions)) {
    throw new Error("initiatedTransactions is not a valid array");
  }

  const transaction =
    transactionToEdit ||
    initiatedTransactions.find((tx) => tx._id === String(transactionId));

  if (!transaction) {
    throw new Error(`Transaction not found for ID: ${transactionId}`);
  }

  const handleClose = () => {
    setShowConfirmationDialog(false);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleTransaction = async () => {
    const deleteTransaction = async () => {
      setSubmitting(true);

      const result = await dispatch(
        deleteInitiatedTransactionChangeAsync({
          companyId: transaction?.companyId,
          initiateId: transaction._id,
        })
      );

      history.push(
        `/companies/${transaction?.companyId}/dashboard?manageYourCompany`
      );
      setUpdateTable(true);
      setShowConfirmationDialog(false);
      setSubmitting(false);
      toast("success", "Transaction has been deleted successfully");
    };

    const updateTransaction = async () => {
      const { idType, officialId: _id } = selectedRow.original;
      let individualOfficials = transaction.individualOfficials || [];
      let corporateOfficials = transaction.corporateOfficials || [];

      if (Constants.INDIVIDUALS_ID_TYPES.includes(idType)) {
        individualOfficials = individualOfficials.filter(
          (official) => official._id !== String(officialId)
        );
      }

      if (Constants.CORPORATES_ID_TYPES.includes(idType)) {
        corporateOfficials = corporateOfficials.filter(
          (official) => official._id !== String(officialId)
        );
      }

      setSubmitting(true);

      const formData = {
        individualOfficials,
        corporateOfficials,
        resolvedFields: [],
        documentId: transaction._id,
      };

      const result = await dispatch(
        editInitiatedTransactionChangeAsync({
          companyId: transaction?.companyId,
          formData,
        })
      );

      if (isInTransactionEditContext) {
        setTransactionToEdit((prev) => ({
          ...prev,
          officials:
            prev.officials?.filter(
              (official) => official._id !== String(officialId)
            ) || [],
        }));
      }

      setUpdateTable(true);
      setChangeList((prev) =>
        prev.filter((item) => item._id !== String(officialId))
      );
      setShowConfirmationDialog(false);
      setSubmitting(false);
      toast("success", "Transaction has been updated successfully");
    };

    try {
      if (transaction.officials.length <= 1) {
        await deleteTransaction();
      } else {
        await updateTransaction();
      }
    } catch (error) {
      setSubmitting(false);
      console.error("Error deleting transaction:", error);
      toast("error", "Failed to delete the transaction. Please try again.");
    }
  };

  return (
    <Dialog open={true} onClose={handleClose}>
      <h2 className="text-xl font-medium text-red-500">{modelText.heading}</h2>
      <div className="py-5 whitespace-normal">
        <span className="font-normal">{modelText.text}</span>
      </div>
      <div className="flex justify-end">
        <div className="p-1">
          <Button
            className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed`}
            onClick={() => setShowConfirmationDialog(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
        <div className="p-1">
          <Button
            className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-red-500 hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 disabled:cursor-not-allowed`}
            onClick={() => {
              handleTransaction();
            }}
            disabled={submitting}
          >
            {submitting ? (
              <div className="flex">
                <SpinnerIcon />
                Deleting...
              </div>
            ) : (
              "I Confirm"
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
